import hr from "./hr.json";
import en from "./en.json";
import type { Locale } from "../constants";

export type Messages = typeof hr;
export type MessageKey = keyof Messages;
/** Base keys that have plural forms (`<base>.one`, `<base>.few`, `<base>.other`). */
export type PluralKey = { [K in MessageKey]: K extends `${infer B}.other` ? B : never }[MessageKey];
export type Params = Record<string, string | number>;

export const dictionaries: Record<Locale, Record<string, string>> = { hr, en };

export function isLocale(x: unknown): x is Locale {
  return x === "hr" || x === "en";
}

function interpolate(s: string, params?: Params): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (_, name: string) => (name in params ? String(params[name]) : `{${name}}`));
}

const pluralRules: Record<Locale, Intl.PluralRules> = {
  hr: new Intl.PluralRules("hr-HR"),
  en: new Intl.PluralRules("en-GB"),
};

export interface Translator {
  locale: Locale;
  t: (key: MessageKey, params?: Params) => string;
  /** Plural: picks `<key>.one|few|other` by `count` and interpolates `{count}`. */
  tp: (key: PluralKey, count: number, params?: Params) => string;
  /** Untyped lookup for keys built at runtime (e.g. enum values); falls back to the key. */
  tx: (key: string, params?: Params) => string;
  has: (key: string) => boolean;
}

export function createTranslator(locale: Locale): Translator {
  const dict = dictionaries[locale];
  const fallback = dictionaries.hr;
  const lookup = (key: string): string | undefined => dict[key] ?? fallback[key];
  const tx = (key: string, params?: Params) => interpolate(lookup(key) ?? key, params);
  return {
    locale,
    t: (key, params) => tx(key, params),
    tx,
    has: (key) => lookup(key) !== undefined,
    tp: (key, count, params) => {
      const cat = pluralRules[locale].select(count);
      const form = lookup(`${key}.${cat}`) ?? lookup(`${key}.other`) ?? key;
      return interpolate(form, { count, ...params });
    },
  };
}

export { hr, en };
